import api from './api';
import { BASE_URL } from '../config';

// Configuration de base
// Pour les fichiers statiques, on utilise le serveur backend sans /api
const API_BASE_URL = BASE_URL;

console.log('🔧 uploadService - API_BASE_URL:', API_BASE_URL);

export interface UploadResponse {
  message: string;
  url: string;
  thumbnailUrl?: string;
  filename?: string;
}

// Upload avatar utilisateur (supporte userId pour la mise à jour d'un tiers par un admin)
export const uploadAvatar = async (file: File, userId?: string | number): Promise<UploadResponse> => {
  const formData = new FormData();
  formData.append('avatar', file);
  if (userId) {
    formData.append('userId', userId.toString());
  }

  const response = await api.post('/upload/avatar', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// Upload image produit
export const uploadProductImage = async (file: File, productId: number): Promise<UploadResponse> => {
  const formData = new FormData();
  formData.append('productImage', file);
  formData.append('productId', productId.toString());

  const response = await api.post('/upload/product', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// Upload image équipement
export const uploadEquipmentImage = async (file: File, equipmentId: number): Promise<UploadResponse> => {
  const formData = new FormData();
  formData.append('equipmentImage', file);
  formData.append('equipmentId', equipmentId.toString());

  const response = await api.post('/upload/equipment', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// Upload document
export const uploadDocument = async (
  file: File,
  type?: string,
  relatedId?: number
): Promise<UploadResponse> => {
  const formData = new FormData();
  formData.append('document', file);
  if (type) formData.append('type', type);
  if (relatedId) formData.append('relatedId', relatedId.toString());

  const response = await api.post('/upload/document', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// Supprimer un fichier
export const deleteUploadedFile = async (type: string, filename: string): Promise<void> => {
  try {
    await api.delete(`/upload/${type}/${filename}`);
  } catch (error: any) {
    // Ignorer 404 : le fichier est déjà absent, pas besoin de bloquer
    if (error?.response?.status !== 404) throw error;
  }
};

// Obtenir l'URL complète d'une image
export const getImageUrl = (path: string): string => {
  if (!path) return '';
  
  let fullUrl = '';
  // Cas 1: URL complète (http://... ou https://...)
  if (path.startsWith('http://') || path.startsWith('https://')) {
    fullUrl = path;
  } else if (path.startsWith('/')) {
    // Cas 2: Chemin relatif commençant par / (/uploads/avatars/...)
    fullUrl = `${API_BASE_URL}${path}`;
  } else if (path.startsWith('uploads/')) {
    // Cas 3: Chemin relatif commençant par uploads/
    fullUrl = `${API_BASE_URL}/${path}`;
  } else {
    // Cas 4: Nom de fichier simple (avatar-40-1777293329555.jpg)
    fullUrl = `${API_BASE_URL}/uploads/avatars/${path}`;
  }

  // Pour les médias protégés (/uploads/interventions/ ou /uploads/documents/), injecter le token JWT d'authentification
  if (fullUrl.includes('/uploads/interventions/') || fullUrl.includes('/uploads/documents/')) {
    const token = typeof window !== 'undefined'
      ? (localStorage.getItem('token') || sessionStorage.getItem('token') || localStorage.getItem('mct_maintenance_token'))
      : null;
    if (token && !fullUrl.includes('token=')) {
      const separator = fullUrl.includes('?') ? '&' : '?';
      fullUrl = `${fullUrl}${separator}token=${encodeURIComponent(token)}`;
    }
  }

  return fullUrl;
};

// Obtenir l'URL du thumbnail
export const getThumbnailUrl = (path: string): string => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  
  const parts = path.split('/');
  const filename = parts[parts.length - 1];
  const directory = parts.slice(0, -1).join('/');
  
  return getImageUrl(`${directory}/thumb-${filename}`);
};

const uploadService = {
  uploadAvatar,
  uploadProductImage,
  uploadEquipmentImage,
  uploadDocument,
  deleteUploadedFile,
  getImageUrl,
  getThumbnailUrl,
};

export default uploadService;
